import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EyeIcon,
  EyeSlashIcon,
  UserPlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ConfirmationModal from './ConfirmationModal';

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();

  // API URL direkt aus Environment oder Fallback
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // URL-Parameter für Referral-Tracking und Club-ID
  const queryParams = new URLSearchParams(location.search);
  const referralSource = queryParams.get('ref') || 'direct';
  const fromDomain = queryParams.get('from_domain');
  const campaign = queryParams.get('campaign');
  const clubId = queryParams.get('club') || process.env.REACT_APP_CLUB_REGISTER_ID;

  // Form State
  const [form, setForm] = useState({
    vorname: '',
    name: '',
    geb_am: '',
    strasse: '',
    plz: '',
    ort: '',
    email: '',
    password: '',
    confirmPassword: '',
    privacyConsent: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [confirmModal, setConfirmModal] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [privacyData, setPrivacyData] = useState<any>(null);

  // Check document referrer for tracking
  useEffect(() => {
    if (!queryParams.get('ref') && document.referrer) {
      try {
        const refererUrl = new URL(document.referrer);
        if (refererUrl.hostname !== window.location.hostname) {
          const newParams = new URLSearchParams(location.search);
          newParams.set('ref', 'website-referer');
          newParams.set('from_domain', refererUrl.hostname);
          const newUrl = `${location.pathname}?${newParams.toString()}`;
          navigate(newUrl, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing referrer:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Fetch privacy policy
  useEffect(() => {
    const fetchPrivacyData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/public/privacy-policy`);

        if (response.data.success && response.data.policy) {
          setPrivacyData({
            version: response.data.policy.version,
            validFrom: response.data.policy.validFrom,
            content: response.data.policy.content
          });
        }
      } catch (err) {
        console.error('Error fetching privacy policy:', err);
      }
    };

    fetchPrivacyData();
  }, [API_URL]);

  // Password strength check
  const checkPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
    return strength >= 3;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setForm({ ...form, password: newPassword });
    checkPasswordStrength(newPassword);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (form.password !== form.confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    // Validate password strength
    if (!checkPasswordStrength(form.password)) {
      setError(
        'Passwort muss mindestens 8 Zeichen lang sein und 3 der folgenden Kriterien erfüllen: Großbuchstabe, Zahl, Sonderzeichen.'
      );
      return;
    }

    // Validate privacy consent
    if (!form.privacyConsent) {
      setError('Bitte stimmen Sie der Datenschutzerklärung zu.');
      return;
    }

    setIsSubmitting(true);
    try {
      // Direkt beim Backend registrieren (ohne Discovery Service)
      console.log('[Register] Registriere bei:', API_URL);
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        vorname: form.vorname,
        name: form.name,
        geb_am: form.geb_am,
        strasse: form.strasse,
        plz: form.plz,
        ort: form.ort,
        email: form.email,
        password: form.password,
        privacyConsent: form.privacyConsent,
        referralSource: referralSource,
        fromDomain: fromDomain,
        campaign: campaign,
        club_id: clubId,
      });

      if (response.data.success) {
        // Navigate to verification code page
        navigate('/verify-code', {
          state: { email: response.data.email || form.email },
          replace: true
        });
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.response?.data?.message || 'Ein Fehler ist bei der Registrierung aufgetreten.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0:
      case 1:
        return 'bg-error-500';
      case 2:
        return 'bg-warning-500';
      case 3:
        return 'bg-success-600';
      case 4:
        return 'bg-success-500';
      default:
        return 'bg-error-500';
    }
  };

  const formatGermanDate = (dateStr: string) => {
    if (!dateStr) return 'Unbekannt';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#0f1419] px-3 py-4 sm:px-4 sm:py-8">
      <div className="w-full max-w-md lg:max-w-xl">
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl sm:rounded-3xl shadow-2xl shadow-gray-200/50 dark:shadow-black/40 border border-gray-100 dark:border-gray-800/50 p-5 sm:p-8 lg:p-10">
          {/* Header */}
          <div className="text-center mb-5 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              Registrieren
            </h1>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mt-1 sm:mt-2">
              Erstelle dein Konto
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-5">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                  Vorname
                </label>
                <input
                  type="text"
                  placeholder="Max"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.vorname}
                  onChange={(e) => setForm({ ...form, vorname: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                  Nachname
                </label>
                <input
                  type="text"
                  placeholder="Mustermann"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Birthdate */}
            <div>
              <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                Geburtsdatum
              </label>
              <input
                type="date"
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.geb_am}
                onChange={(e) => setForm({ ...form, geb_am: e.target.value })}
                required
              />
            </div>

            {/* Address */}
            <div>
              <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                Straße und Hausnummer
              </label>
              <input
                type="text"
                placeholder="Musterstraße 1"
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.strasse}
                onChange={(e) => setForm({ ...form, strasse: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                  PLZ
                </label>
                <input
                  type="text"
                  placeholder="12345"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.plz}
                  onChange={(e) => setForm({ ...form, plz: e.target.value })}
                  pattern="[0-9]{5}"
                  required
                />
              </div>
              <div>
                <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                  Ort
                </label>
                <input
                  type="text"
                  placeholder="Musterstadt"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                  value={form.ort}
                  onChange={(e) => setForm({ ...form, ort: e.target.value })}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                E-Mail
              </label>
              <input
                type="email"
                placeholder="name@beispiel.de"
                className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                Passwort
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all pr-12"
                  value={form.password}
                  onChange={handlePasswordChange}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
              <div className="mt-2">
                <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
                    style={{ width: `${passwordStrength * 25}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1.5">
                  Min. 8 Zeichen, Großbuchstabe, Zahl, Sonderzeichen
                </p>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-[11px] sm:text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-1 sm:mb-2">
                Passwort bestätigen
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full px-3 py-2.5 sm:px-4 sm:py-3.5 bg-gray-50 dark:bg-[#0f1419] border-0 rounded-lg sm:rounded-xl text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white dark:focus:bg-[#1a2332] transition-all pr-12"
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Privacy Consent */}
            <div>
              <label className="flex items-start space-x-3 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-0 rounded bg-gray-200 dark:bg-gray-700"
                  checked={form.privacyConsent}
                  onChange={(e) => setForm({ ...form, privacyConsent: e.target.checked })}
                  required
                />
                <span className="text-gray-600 dark:text-gray-400">
                  Ich stimme der{' '}
                  <button
                    type="button"
                    className="text-primary-600 dark:text-primary-400 hover:underline"
                    onClick={() => setShowPrivacyModal(true)}
                  >
                    Datenschutzerklärung
                  </button>{' '}
                  zu.
                </span>
              </label>
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl flex items-start gap-3 animate-shake">
                <svg className="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                </svg>
                <span className="text-error-700 dark:text-error-300 text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:bg-primary-400 disabled:cursor-not-allowed text-white py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-200 hover:shadow-lg hover:shadow-primary-600/25 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Registrierung wird verarbeitet...
                </>
              ) : (
                'Registrieren'
              )}
            </button>

            {/* Divider */}
            <div className="relative my-3 sm:my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-[#1c2432] px-4 text-gray-400 dark:text-gray-500 tracking-wider">
                  oder
                </span>
              </div>
            </div>

            {/* Login Link */}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-2.5 sm:py-3.5 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium hover:bg-gray-50 dark:hover:bg-[#0f1419] transition-all duration-200"
            >
              <UserPlusIcon className="h-5 w-5" />
              Zum Login
            </button>
          </form>
        </div>
      </div>

      {/* Privacy Modal */}
      {showPrivacyModal && privacyData && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white dark:bg-[#1c2432] rounded-t-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden border border-gray-100 dark:border-gray-800/50">
            <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800/50">
              <div className="flex justify-between items-center">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
                  Datenschutzerklärung
                </h2>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6 overflow-y-auto max-h-[60vh]">
              {privacyData?.version ? (
                <>
                  <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                    Version {privacyData.version}, gültig ab {formatGermanDate(privacyData.validFrom)}
                  </p>
                  {privacyData.content && (
                    <div
                      className={[
                        'text-sm leading-relaxed text-gray-700 dark:text-gray-300',
                        '[&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-gray-900 dark:[&_h2]:text-white [&_h2]:mt-6 [&_h2]:mb-3 [&_h2]:first:mt-0',
                        '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 dark:[&_h3]:text-white [&_h3]:mt-4 [&_h3]:mb-2',
                        '[&_p]:mb-3',
                        '[&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ul]:space-y-1',
                        '[&_li]:text-gray-700 dark:[&_li]:text-gray-300',
                        '[&_strong]:font-semibold [&_strong]:text-gray-900 dark:[&_strong]:text-white',
                        '[&_em]:italic',
                        '[&_a]:text-primary-600 dark:[&_a]:text-primary-400 [&_a]:underline hover:[&_a]:text-primary-700'
                      ].join(' ')}
                      dangerouslySetInnerHTML={{ __html: privacyData.content }}
                    />
                  )}
                </>
              ) : (
                <p className="text-gray-600 dark:text-gray-400">
                  Keine aktuelle Datenschutzerklärung gefunden.
                </p>
              )}
            </div>
            <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-800/50">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2.5 sm:py-3 rounded-lg sm:rounded-xl text-sm sm:text-base font-medium transition-all duration-200"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmModal && (
        <ConfirmationModal
          visible={confirmModal.visible}
          onClose={() => setConfirmModal(null)}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          variant={confirmModal.variant}
          button={confirmModal.button}
          buttons={confirmModal.buttons}
        />
      )}
    </div>
  );
}
